import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSimplifiedUserData } from './SimplifiedUserDataProvider';

type Theme = 'light' | 'dark' | 'auto';

interface ThemeContextType {
  theme: Theme;
  effectiveTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { currentUser } = useSimplifiedUserData();
  const [theme, setTheme] = useState<Theme>('light');
  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>('light');

  // Get the system preference
  const getSystemPreference = (): 'light' | 'dark' => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  };

  // Calculate effective theme based on user setting and system preference
  const calculateEffectiveTheme = (userTheme: Theme): 'light' | 'dark' => {
    if (userTheme === 'auto') {
      return getSystemPreference();
    }
    return userTheme;
  };

  // Update theme when current user changes
  useEffect(() => {
    if (currentUser?.settings?.theme) {
      const userTheme = currentUser.settings.theme as Theme;
      setTheme(userTheme);
      const newEffectiveTheme = calculateEffectiveTheme(userTheme);
      setEffectiveTheme(newEffectiveTheme);
    }
  }, [currentUser]);

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (theme !== 'auto') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setEffectiveTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Remove any existing theme classes
    root.classList.remove('light', 'dark');
    
    // Add the effective theme class
    root.classList.add(effectiveTheme);
    
    // Store in localStorage for persistence across page reloads
    localStorage.setItem('effective-theme', effectiveTheme);
  }, [effectiveTheme]);

  // Initialize theme from localStorage on first load (before user data is available)
  useEffect(() => {
    const savedTheme = localStorage.getItem('effective-theme') as 'light' | 'dark' | null;
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      setEffectiveTheme(savedTheme);
      document.documentElement.classList.add(savedTheme);
    }
  }, []);

  const contextValue: ThemeContextType = {
    theme,
    effectiveTheme,
    setTheme: (newTheme: Theme) => {
      setTheme(newTheme);
      const newEffectiveTheme = calculateEffectiveTheme(newTheme);
      setEffectiveTheme(newEffectiveTheme);
    },
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
